import { useEffect, useState } from "react";

import { UseQueryResult } from "@tanstack/react-query";

import { NO_FOLDER_FILTER } from "src/api";
import { LoadingView } from "src/base-components/LoadingView";
import {
  FlowFolderListItem,
  FlowFolderListResponse,
} from "src/clients/flow-api";
import { toastActions } from "src/design-system/Toast/utils";
import { AddFolder } from "src/flowsOverview/v2/AddFolder";
import { Folder } from "src/flowsOverview/v2/Folder";
import { FolderListSkeleton } from "src/flowsOverview/v2/FolderListSkeleton";
import { DeleteFolderModal } from "src/flowsOverview/v2/folderModals/DeleteFolderModal";
import { EditFolderModal } from "src/flowsOverview/v2/folderModals/EditFolderModal";
import {
  useCreateFolder,
  useDeleteFolder,
  useEditFolder,
} from "src/flowsOverview/v2/folderQueries";
import { useLatestViewedFolder } from "src/flowsOverview/v2/useLatestViewedFolder";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useFolderIdSearchParam } from "src/router/SearchParams";
import {
  getBaseUrl,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { wrapWithAxiosResponseErrorHandler } from "src/utils/toastError";

type Props = {
  organizationId: string;
  workspaceId: string;
  folderResult: UseQueryResult<FlowFolderListResponse, Error>;
};

export const FolderList: React.FC<Props> = ({
  organizationId,
  workspaceId,
  folderResult,
}) => {
  const [selectedFolderParam, setSelectedFolderParam] =
    useFolderIdSearchParam();
  const { flowFolders } = useCapabilities();
  const createFolderMutations = useCreateFolder();
  const editFolderMutations = useEditFolder(workspaceId);
  const deleteFolderMutations = useDeleteFolder(workspaceId);
  const userId = useCurrentUserId();

  const [latestViewedFolder, setLatestViewedFolder] = useLatestViewedFolder({
    userId: userId!,
    workspaceId,
  });

  const [createFolderModalOpen, setCreateFolderModalOpen] =
    useState<boolean>(false);

  const [folderToEdit, setFolderToEdit] = useState<FlowFolderListItem>();
  const [editFolderModalOpen, setEditFolderModalOpen] =
    useState<boolean>(false);

  const [folderToDelete, setFolderToDelete] = useState<FlowFolderListItem>();
  const [deleteFolderModalOpen, setDeleteFolderModalOpen] =
    useState<boolean>(false);

  const onRequestDeleteFolder = (folder: FlowFolderListItem) => {
    setFolderToDelete(folder);
    setDeleteFolderModalOpen(true);
  };

  const onCopyLink = (folder: FlowFolderListItem | undefined) => {
    copyTextToClipboard(
      getBaseUrl() +
        getUrlToWsDashboard({
          orgId: organizationId,
          wsId: workspaceId,
          page: WsDashboardPageCode.Flows,
          folderId: folder?.id,
        }),
    ).then(() => {
      toastActions.success({
        title: "Link copied to clipboard",
      });
    });
  };

  const onRequestSelect = (folderId: string) => {
    setSelectedFolderParam(folderId);
  };

  useEffect(() => {
    const getFirstNonEmptyFolder = () =>
      folderResult?.data?.folders.find((folder) => folder.flow_count > 0)?.id;

    const goToFirstNonEmptyFolder = () => {
      if (folderResult.data) {
        const folderId = getFirstNonEmptyFolder() ?? NO_FOLDER_FILTER;
        setSelectedFolderParam(folderId);
        setLatestViewedFolder(folderId);
      }
    };

    if (!selectedFolderParam && folderResult.data) {
      if (
        latestViewedFolder &&
        folderResult.data.folders.some(
          (folder) => folder.id === latestViewedFolder,
        )
      ) {
        setSelectedFolderParam(latestViewedFolder);
      } else {
        goToFirstNonEmptyFolder();
      }
    }
    if (selectedFolderParam && selectedFolderParam !== latestViewedFolder) {
      setLatestViewedFolder(selectedFolderParam);
    }
  }, [
    selectedFolderParam,
    folderResult.data,
    latestViewedFolder,
    setSelectedFolderParam,
    folderResult,
    setLatestViewedFolder,
  ]);

  return (
    <div className="decideScrollbar h-full min-h-0 w-70">
      <ul className="col-span-1 flex flex-col gap-y-3">
        <LoadingView
          queryResult={folderResult}
          renderUpdated={(data: FlowFolderListResponse) => (
            <>
              {data.folders.map((folder) => (
                <Folder
                  key={folder.id}
                  folder={folder}
                  selected={folder.id === selectedFolderParam}
                  onCopyLink={onCopyLink}
                  onRequestDelete={onRequestDeleteFolder}
                  onRequestEdit={() => {
                    setFolderToEdit(folder);
                    setEditFolderModalOpen(true);
                  }}
                  onRequestSelect={onRequestSelect}
                />
              ))}
              <Folder
                count={data.flows_outside_folders_count}
                folder={undefined}
                selected={selectedFolderParam === NO_FOLDER_FILTER}
                onCopyLink={onCopyLink}
                onRequestDelete={onRequestDeleteFolder}
                onRequestSelect={() => {
                  onRequestSelect(NO_FOLDER_FILTER);
                }}
              />
              {flowFolders.canCreate && (
                <AddFolder
                  onRequestAddFolder={() => {
                    setCreateFolderModalOpen(true);
                  }}
                />
              )}
            </>
          )}
          renderUpdating={() => <FolderListSkeleton />}
        />
      </ul>
      <EditFolderModal
        mode="create"
        open={createFolderModalOpen}
        onClose={() => setCreateFolderModalOpen(false)}
        onConfirm={async (name: string) => {
          await createFolderMutations.mutateAsync({
            name,
            workspace_id: workspaceId,
          });
        }}
      />
      <EditFolderModal
        afterLeave={() => {
          setFolderToEdit(undefined);
        }}
        folder={folderToEdit}
        mode="edit"
        open={editFolderModalOpen}
        onClose={() => setEditFolderModalOpen(false)}
        onConfirm={async (name: string) => {
          if (folderToEdit) {
            await editFolderMutations.mutateAsync({
              folderId: folderToEdit.id,
              flowFolderUpdate: { name },
            });
          }
        }}
      />
      <DeleteFolderModal
        afterLeave={() => {
          setFolderToDelete(undefined);
        }}
        folder={folderToDelete}
        isOpen={deleteFolderModalOpen}
        onClose={() => {
          setDeleteFolderModalOpen(false);
        }}
        onConfirm={wrapWithAxiosResponseErrorHandler(async () => {
          if (!folderToDelete) return;

          await deleteFolderMutations.mutateAsync(folderToDelete.id);
          setSelectedFolderParam(NO_FOLDER_FILTER);
          setDeleteFolderModalOpen(false);
        })}
      />
    </div>
  );
};
